import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'

export interface TimeRange {
  type: 'relative' | 'absolute'
  relative?: {
    amount: number
    unit: 'minute' | 'hour' | 'day' | 'week' | 'month'
    label: string
  }
  absolute?: {
    startDate: string
    endDate: string
  }
}

export interface DashboardFilter {
  id: string
  type: 'asset' | 'property' | 'tag'
  label: string
  values: string[]
  operator: 'equals' | 'contains' | 'in'
}

export interface DashboardContextType {
  // Time Controls
  timeRange: TimeRange
  setTimeRange: (timeRange: TimeRange) => void
  refreshInterval: number // seconds, 0 = no auto-refresh
  setRefreshInterval: (interval: number) => void
  
  // Manual Refresh
  refreshTrigger: number // increment to trigger refresh
  triggerRefresh: () => void
  
  // Filters
  filters: DashboardFilter[]
  setFilters: (filters: DashboardFilter[]) => void
  addFilter: (filter: DashboardFilter) => void
  removeFilter: (filterId: string) => void
  
  // Dashboard State
  isRefreshing: boolean
  setIsRefreshing: (refreshing: boolean) => void
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined)

// Default time range options (Grafana-style)
export const DEFAULT_TIME_RANGES: TimeRange[] = [
  {
    type: 'relative',
    relative: { amount: 5, unit: 'minute', label: 'Last 5 minutes' }
  },
  {
    type: 'relative',
    relative: { amount: 15, unit: 'minute', label: 'Last 15 minutes' }
  },
  {
    type: 'relative',
    relative: { amount: 30, unit: 'minute', label: 'Last 30 minutes' }
  },
  {
    type: 'relative',
    relative: { amount: 1, unit: 'hour', label: 'Last 1 hour' }
  },
  {
    type: 'relative',
    relative: { amount: 6, unit: 'hour', label: 'Last 6 hours' }
  },
  {
    type: 'relative',
    relative: { amount: 12, unit: 'hour', label: 'Last 12 hours' }
  },
  {
    type: 'relative',
    relative: { amount: 24, unit: 'hour', label: 'Last 24 hours' }
  },
  {
    type: 'relative',
    relative: { amount: 7, unit: 'day', label: 'Last 7 days' }
  },
  {
    type: 'relative',
    relative: { amount: 30, unit: 'day', label: 'Last 30 days' }
  }
]

// Default refresh intervals (seconds)
export const DEFAULT_REFRESH_INTERVALS = [
  { value: 0, label: 'Off' },
  { value: 5, label: '5s' },
  { value: 10, label: '10s' },
  { value: 30, label: '30s' },
  { value: 60, label: '1m' },
  { value: 300, label: '5m' },
  { value: 900, label: '15m' },
  { value: 1800, label: '30m' },
  { value: 3600, label: '1h' }
]

interface DashboardProviderProps {
  children: ReactNode
  initialTimeRange?: TimeRange
  initialRefreshInterval?: number
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({
  children,
  initialTimeRange = DEFAULT_TIME_RANGES[6], // Last 24 hours
  initialRefreshInterval = 0 // Off by default
}) => {
  const [timeRange, setTimeRange] = useState<TimeRange>(initialTimeRange)
  const [refreshInterval, setRefreshInterval] = useState(initialRefreshInterval)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [filters, setFilters] = useState<DashboardFilter[]>([])
  const [isRefreshing, setIsRefreshing] = useState(false)

  const triggerRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1)
  }, [])

  const addFilter = useCallback((filter: DashboardFilter) => {
    setFilters(prev => {
      // Replace if same ID exists, otherwise add
      const existing = prev.findIndex(f => f.id === filter.id)
      if (existing >= 0) {
        const updated = [...prev]
        updated[existing] = filter
        return updated
      }
      return [...prev, filter]
    })
  }, [])

  const removeFilter = useCallback((filterId: string) => {
    setFilters(prev => prev.filter(f => f.id !== filterId))
  }, [])

  const value: DashboardContextType = {
    timeRange,
    setTimeRange,
    refreshInterval,
    setRefreshInterval,
    refreshTrigger,
    triggerRefresh,
    filters,
    setFilters,
    addFilter,
    removeFilter,
    isRefreshing,
    setIsRefreshing
  }

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  )
}

export const useDashboardContext = (): DashboardContextType => {
  const context = useContext(DashboardContext)
  if (!context) {
    throw new Error('useDashboardContext must be used within a DashboardProvider')
  }
  return context
}

// Helper function to convert TimeRange to actual dates
export const getTimeRangeDates = (timeRange: TimeRange): { start: Date; end: Date } => {
  if (timeRange.type === 'absolute' && timeRange.absolute) {
    return {
      start: new Date(timeRange.absolute.startDate),
      end: new Date(timeRange.absolute.endDate)
    }
  }

  if (timeRange.type === 'relative' && timeRange.relative) {
    const end = new Date()
    const start = new Date()

    switch (timeRange.relative.unit) {
      case 'minute':
        start.setMinutes(start.getMinutes() - timeRange.relative.amount)
        break
      case 'hour':
        start.setHours(start.getHours() - timeRange.relative.amount)
        break
      case 'day':
        start.setDate(start.getDate() - timeRange.relative.amount)
        break
      case 'week':
        start.setDate(start.getDate() - (timeRange.relative.amount * 7))
        break
      case 'month':
        start.setMonth(start.getMonth() - timeRange.relative.amount)
        break
    }

    return { start, end }
  }

  // Fallback to last 24 hours
  const end = new Date()
  const start = new Date()
  start.setHours(start.getHours() - 24)
  return { start, end }
}